import type { MisconceptionDefinition } from './types'

/** TIJD — Tijd misconcepties. */
export const TIJD_MISCONCEPTIONS: MisconceptionDefinition[] = [
  // ───────────────────────────────────────────────────────────
  // Snelheid / formules
  // ───────────────────────────────────────────────────────────
  {
    id: 'tijd.speed-formula-confused',
    label: 'Snelheidsformule verward',
    module: 'Tijd',
    description:
      'Leerling gebruikt de verkeerde bewerking (bijv. vermenigvuldigen in plaats van delen) bij de AST-driehoek.',
    typicalFamilies: ['TIJD-SNELHEID', 'TIJD-AFSTAND', 'TIJD-GEMIDDELDE'],
  },
  {
    id: 'tijd.minutes-to-hours-error',
    label: 'Minuten naar uren fout',
    module: 'Tijd',
    description:
      'Leerling rekent minuten verkeerd om naar decimale uren (bijv. 15 min = 0,15 uur in plaats van 0,25 uur).',
    typicalFamilies: ['TIJD-SNELHEID', 'TIJD-AFSTAND', 'TIJD-GEMIDDELDE'],
  },

  // ───────────────────────────────────────────────────────────
  // Klok-lezen: digitaal & 12/24-uurs (Ronde-5 additions, mei 2026)
  // ───────────────────────────────────────────────────────────
  {
    id: 'tijd.00-vs-12-verward',
    label: '00:00 verward met 12:00',
    module: 'Tijd',
    description:
      'Leerling leest 00:00 als 12:00 (of omgekeerd) bij 24-uurs digitale tijden, door verwarring met de 12-uurs notatie.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-12-24'],
  },
  {
    id: 'tijd.middernacht-vs-01:00',
    label: 'Middernacht verward met 01:00',
    module: 'Tijd',
    description:
      'Leerling denkt dat 01:00 middernacht is, of dat middernacht 01:00 is — de overgang naar de nieuwe dag wordt verkeerd geplaatst.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-12-24'],
  },
  {
    id: 'tijd.24:00-bestaat-niet',
    label: '24:00 als geldige tijd gelezen',
    module: 'Tijd',
    description:
      'Leerling schrijft of accepteert 24:00 als geldige notatie, terwijl 24:00 als 00:00 van de volgende dag wordt genoteerd.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-12-24'],
  },
  {
    id: 'tijd.kwart-voor-over-verward',
    label: '"Kwart voor" verward met "kwart over"',
    module: 'Tijd',
    description:
      'Leerling verwisselt "kwart voor" en "kwart over" (bijv. kwart voor 4 als 4:15 i.p.v. 3:45).',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.half-voor-over-blijft-op-uur',
    label: '"Half" op verkeerd uur geplaatst',
    module: 'Tijd',
    description:
      'Leerling leest "half drie" als 3:30 in plaats van 2:30 — de Nederlandse conventie (half = uur eerder) wordt niet toegepast.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.minuten-voor-na-half-verward',
    label: 'Minuten voor/na half door elkaar',
    module: 'Tijd',
    description:
      'Leerling verwart "tien voor half" en "tien over half" of telt minuten in de verkeerde richting rond het hele en halve uur.',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.dagdeel-verward',
    label: 'Dagdeel (am/pm) verward',
    module: 'Tijd',
    description:
      'Leerling verwart ochtend/middag/avond bij het omrekenen tussen 12- en 24-uurs notatie (bijv. 19:00 als 7:00 ’s ochtends).',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-12-24'],
  },
  {
    id: 'tijd.kwart-vs-tien-voor',
    label: '"Kwart voor" verward met "tien voor"',
    module: 'Tijd',
    description:
      'Leerling verwart kwart (15 min) en tien minuten bij benoemingen als "kwart voor" vs "tien voor".',
    typicalFamilies: ['TIJD-DIGITAAL', 'TIJD-ANALOOG'],
  },
  {
    id: 'tijd.half-verkeerd-uur',
    label: '"Half" gekoppeld aan verkeerd uur',
    module: 'Tijd',
    description:
      'Bij conversie of klok-lezen plaatst de leerling "half" bij het verkeerde hele uur (variant van half-voor-over-blijft-op-uur, bij 12↔24 omrekenen).',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-DIGITAAL'],
  },
  {
    id: 'tijd.pm-als-am',
    label: 'PM gelezen als AM',
    module: 'Tijd',
    description:
      'Leerling negeert het pm-deel bij conversie naar 24-uurs notatie (bijv. 3 pm als 03:00 i.p.v. 15:00).',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-12-24'],
  },
  {
    id: 'tijd.minuten-genegeerd',
    label: 'Minuten genegeerd bij conversie',
    module: 'Tijd',
    description:
      'Leerling rondt naar het hele uur en negeert de minuten bij het omrekenen tussen tijdnotaties.',
    typicalFamilies: ['TIJD-CONVERSIE', 'TIJD-12-24'],
  },

  // ───────────────────────────────────────────────────────────
  // Tijdzones
  // ───────────────────────────────────────────────────────────
  {
    id: 'tijd.timezone-direction-confused',
    label: 'Tijdzone richting verward',
    module: 'Tijd',
    description:
      'Leerling telt uren op in plaats van af te trekken bij tijdzones, of andersom.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },
  {
    id: 'tijd.timezone-midnight-error',
    label: 'Tijdzone over middernacht',
    module: 'Tijd',
    description:
      'Leerling houdt geen rekening met de 24-uurs grens bij tijdzones.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },
  {
    id: 'tijd.timezone-flight-error',
    label: 'Tijdverschil vergeten bij vlucht',
    module: 'Tijd',
    description:
      'Leerling vergeet het tijdverschil op te tellen of af te trekken bij het berekenen van de aankomsttijd na een vlucht.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },

  // ───────────────────────────────────────────────────────────
  // Tijdsduur / minuten rekenen (SCHEMA + PLANNING + DIAGRAM)
  // ───────────────────────────────────────────────────────────
  {
    id: 'tijd.minutes-subtraction-error',
    label: 'Minuten verkeerd afgetrokken',
    module: 'Tijd',
    description:
      'Leerling trekt minuten verkeerd af bij tijdsduur-berekeningen (te weinig of te veel).',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-DUUR', 'TIJD-DIAGRAM'],
  },
  {
    id: 'tijd.minutes-addition-error',
    label: 'Minuten verkeerd opgeteld',
    module: 'Tijd',
    description:
      'Leerling telt minuten verkeerd op bij eindtijd- of wachttijd-berekeningen.',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-PLANNING', 'TIJD-DIAGRAM'],
  },
  {
    id: 'tijd.cross-hour-only-first-part',
    label: 'Uurgrens: alleen eerste deel meegeteld',
    module: 'Tijd',
    description:
      'Bij een tijdsduur over de uurgrens telt de leerling alleen het deel tot het hele uur (of alleen het deel ná het hele uur).',
    typicalFamilies: [
      'TIJD-SCHEMA',
      'TIJD-DUUR',
      'TIJD-PLANNING',
      'TIJD-DIAGRAM',
    ],
  },
  {
    id: 'tijd.extra-hour-added',
    label: 'Extra uur ten onrechte meegeteld',
    module: 'Tijd',
    description:
      'Leerling telt een extra uur mee terwijl begin- en eindtijd in hetzelfde uur (of binnen 1 uur) liggen.',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-DUUR'],
  },
  {
    id: 'tijd.add-instead-of-subtract',
    label: 'Optellen i.p.v. aftrekken bij "eerder"',
    module: 'Tijd',
    description:
      'Bij vragen waarin de leerling eerder moet zijn of terug moet rekenen, telt zij de tijd op in plaats van af te trekken.',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-PLANNING'],
  },
  {
    id: 'tijd.full-hour-instead-of-minutes',
    label: 'Heel uur i.p.v. minuten gerekend',
    module: 'Tijd',
    description:
      'Leerling trekt een heel uur af of telt een heel uur op terwijl de vraag om minuten gaat.',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-PLANNING'],
  },
  {
    id: 'tijd.buffer-time-forgotten',
    label: 'Buffertijd vergeten',
    module: 'Tijd',
    description:
      'Leerling vergeet bij plannings-vraagstukken de buffer- of inchecktijd mee te rekenen.',
    typicalFamilies: ['TIJD-PLANNING'],
  },
  {
    id: 'tijd.overflow-notation-error',
    label: 'Overflow-notatie fout (>60 minuten)',
    module: 'Tijd',
    description:
      'Leerling schrijft >60 minuten direct achter de dubbele punt (bijv. "13:75" of "11:70") in plaats van om te rekenen naar uren en minuten.',
    typicalFamilies: ['TIJD-PLANNING', 'TIJD-DUUR'],
  },
  {
    id: 'tijd.ignores-partial-hour',
    label: 'Deel-uur genegeerd in tijdsduur',
    module: 'Tijd',
    description:
      'Leerling telt alleen hele uren en negeert de minuten boven of onder het hele uur bij duur-berekeningen.',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.rounds-up-to-full-hour',
    label: 'Tijdsduur opgerond naar heel uur',
    module: 'Tijd',
    description:
      'Leerling rondt een gemengde tijdsduur naar boven af op een heel uur (bijv. 1u30 → 2 uur).',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.counts-only-minutes',
    label: 'Alleen minuten geteld bij duur',
    module: 'Tijd',
    description:
      'Leerling telt alleen het minuten-deel van de duur en vergeet het hele uur.',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.wrong-minutes-to-hour',
    label: 'Minuten tot heel uur verkeerd geteld',
    module: 'Tijd',
    description:
      'Leerling berekent het aantal minuten van een startpunt tot het volgende hele uur fout (bijv. :45 → :00 als 5 min i.p.v. 15 min).',
    typicalFamilies: ['TIJD-DUUR', 'TIJD-PLANNING'],
  },
  {
    id: 'tijd.counts-too-many-hours',
    label: 'Te veel hele uren geteld',
    module: 'Tijd',
    description:
      'Leerling telt meer hele uren dan er werkelijk tussen begin- en eindtijd liggen.',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.addition-error-minutes',
    label: 'Rekenfout bij optellen van deelminuten',
    module: 'Tijd',
    description:
      'Leerling maakt een rekenfout bij het optellen van twee groepen minuten (bijv. 45 + 40 = 95 i.p.v. 85).',
    typicalFamilies: ['TIJD-DUUR', 'TIJD-PLANNING'],
  },
  {
    id: 'tijd.wrong-minutes-read',
    label: 'Verkeerd aantal minuten afgelezen',
    module: 'Tijd',
    description:
      'Leerling leest een verkeerd aantal minuten uit de opgave (bijv. 15 i.p.v. 45 minuten).',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.confuses-half-quarter',
    label: 'Half en kwart verward bij duur',
    module: 'Tijd',
    description:
      'Leerling verwart 30 minuten (half) met 45 minuten (drie kwartier) of 15 minuten (kwart) bij duur-berekeningen.',
    typicalFamilies: ['TIJD-DUUR'],
  },

  // ───────────────────────────────────────────────────────────
  // Kalender
  // ───────────────────────────────────────────────────────────
  {
    id: 'tijd.weekday-off-by-one',
    label: 'Dag van de week off-by-one',
    module: 'Tijd',
    description:
      'Leerling telt een dag te veel of te weinig bij het bepalen van een dag-van-de-week (bijv. één dag eerder of later).',
    typicalFamilies: ['TIJD-KALENDER'],
  },
  {
    id: 'tijd.week-same-weekday-missed',
    label: 'Zelfde-dag-bij-veelvoud-van-7 gemist',
    module: 'Tijd',
    description:
      'Leerling beseft niet dat n × 7 dagen verder altijd dezelfde dag van de week oplevert.',
    typicalFamilies: ['TIJD-KALENDER'],
  },
  {
    id: 'tijd.month-boundary-not-applied',
    label: 'Maand- of jaargrens niet toegepast',
    module: 'Tijd',
    description:
      'Leerling rekent door over de 30/31/28-dagen grens van een maand alsof elke maand even lang is.',
    typicalFamilies: ['TIJD-KALENDER'],
  },
  {
    id: 'tijd.calendar-date-arithmetic-error',
    label: 'Datum-rekenfout',
    module: 'Tijd',
    description:
      'Leerling rekent verkeerd met de datum (bijv. 3 + 7 = 9 in plaats van 10, of 17 − 5 = 11 in plaats van 12).',
    typicalFamilies: ['TIJD-KALENDER'],
  },

  // ───────────────────────────────────────────────────────────
  // Diagram-lezen
  // ───────────────────────────────────────────────────────────
  {
    id: 'tijd.diagram-rise-vs-fall-confused',
    label: 'Stijging vs. daling verward in diagram',
    module: 'Tijd',
    description:
      'Leerling herkent het verschil tussen een stijgende en dalende lijn in een tijddiagram niet, of telt een daling als stijging.',
    typicalFamilies: ['TIJD-DIAGRAM'],
  },
  {
    id: 'tijd.diagram-speed-vs-distance-confused',
    label: 'Snelheid vs. afstand verward in afstand-tijd-grafiek',
    module: 'Tijd',
    description:
      'In een afstand-tijd diagram trekt de leerling de verkeerde conclusie over snelheid (bijv. minder afstand = sneller).',
    typicalFamilies: ['TIJD-DIAGRAM'],
  },
  {
    id: 'tijd.diagram-departure-vs-arrival-confused',
    label: 'Vertrek- vs. aankomsttijd verward',
    module: 'Tijd',
    description:
      'Leerling vergelijkt vertrektijden in plaats van aankomsttijden (of andersom) bij vergelijkings-vragen.',
    typicalFamilies: ['TIJD-DIAGRAM', 'TIJD-SCHEMA'],
  },

  // ───────────────────────────────────────────────────────────
  // Omrekenen: uren ↔ minuten ↔ seconden ↔ dagen
  // ───────────────────────────────────────────────────────────

  // Uren → minuten
  {
    id: 'tijd.uur-min-keer-10',
    label: 'Uren × 10 i.p.v. × 60',
    module: 'Tijd',
    description:
      'Leerling vermenigvuldigt uren met 10 in plaats van met 60 bij omrekenen naar minuten.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.uur-min-keer-100',
    label: 'Uren × 100 i.p.v. × 60',
    module: 'Tijd',
    description:
      'Leerling vermenigvuldigt uren met 100 in plaats van met 60 (verwarring met decimaal stelsel).',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.uur-min-dubbeltellen',
    label: 'Uren dubbel geteld',
    module: 'Tijd',
    description:
      'Leerling vermenigvuldigt het resultaat van uren × 60 nogmaals met 2 of telt het dubbel.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },

  // Minuten → uren
  {
    id: 'tijd.min-uur-deel-120',
    label: 'Minuten ÷ 120 i.p.v. ÷ 60',
    module: 'Tijd',
    description:
      'Leerling deelt minuten door 120 in plaats van door 60 bij omrekenen naar uren.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-uur-cijfers-omdraaien',
    label: 'Cijfers omgedraaid bij min→uur',
    module: 'Tijd',
    description:
      'Leerling pakt de eerste cijfers van het minuten-getal als antwoord (bijv. 240 min → 24 uur).',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-uur-deel-6',
    label: 'Minuten ÷ 6 i.p.v. ÷ 60',
    module: 'Tijd',
    description:
      'Leerling deelt minuten door 6 in plaats van door 60 (een nul vergeten).',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },

  // Seconden → minuten
  {
    id: 'tijd.sec-min-deel-6',
    label: 'Seconden ÷ 6 i.p.v. ÷ 60',
    module: 'Tijd',
    description:
      'Leerling deelt seconden door 6 in plaats van door 60 bij omrekenen naar minuten.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.sec-min-deel-100',
    label: 'Seconden ÷ 100 i.p.v. ÷ 60',
    module: 'Tijd',
    description:
      'Leerling deelt seconden door 100 in plaats van door 60 (verwarring met decimaal stelsel).',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.sec-min-deel-10',
    label: 'Seconden ÷ 10 i.p.v. ÷ 60',
    module: 'Tijd',
    description:
      'Leerling deelt seconden door 10 in plaats van door 60 bij omrekenen naar minuten.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.sec-min-rekenfout-4',
    label: 'Deelfout sec→min (4 i.p.v. 7)',
    module: 'Tijd',
    description:
      'Leerling maakt een deelfout bij seconden ÷ 60 en komt op een te laag aantal minuten uit.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },

  // Gemengd uur+min → minuten
  {
    id: 'tijd.gemengd-cijfers-plakken',
    label: 'Cijfers geplakt bij gemengde tijd',
    module: 'Tijd',
    description:
      'Leerling plakt de uren en losse minuten aan elkaar (bijv. 2u30min → 230 minuten) in plaats van te rekenen.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.gemengd-1-uur-vergeten',
    label: 'Eén uur vergeten in gemengde tijd',
    module: 'Tijd',
    description:
      'Leerling rekent maar één uur i.p.v. het volledige aantal uren bij omrekenen van gemengde tijd naar minuten.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.gemengd-keer-100',
    label: 'Gemengde tijd × 100 i.p.v. × 60',
    module: 'Tijd',
    description:
      'Leerling vermenigvuldigt de uren met 100 in plaats van met 60 bij omrekenen van gemengde tijd.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },

  // Gemengd min+sec → seconden
  {
    id: 'tijd.gemengd-sec-plakken',
    label: 'Cijfers geplakt (min+sec → sec)',
    module: 'Tijd',
    description:
      'Leerling plakt de minuten en losse seconden aan elkaar i.p.v. te rekenen (bijv. 3min20sec → 320 seconden).',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.gemengd-sec-keer-20',
    label: 'Minuten × 20 i.p.v. × 60',
    module: 'Tijd',
    description:
      'Leerling vermenigvuldigt minuten met het getal seconden uit de vraag i.p.v. met 60.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.gemengd-sec-2-min',
    label: 'Verkeerd aantal minuten gerekend',
    module: 'Tijd',
    description:
      'Leerling rekent met één minuut minder dan in de vraag staat bij min+sec → seconden.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.gemengd-sec-plakken-245',
    label: 'Cijfers geplakt (2+45=245)',
    module: 'Tijd',
    description:
      'Leerling plakt minuten en seconden aan elkaar bij omrekenen naar seconden (variant van cijfers-plakken).',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.gemengd-sec-1-min',
    label: 'Eén minuut gerekend i.p.v. twee',
    module: 'Tijd',
    description:
      'Leerling neemt slechts 1 minuut mee in plaats van het werkelijke aantal minuten uit de vraag.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.gemengd-sec-4-min',
    label: 'Vier minuten gerekend i.p.v. twee',
    module: 'Tijd',
    description:
      'Leerling rekent met meer minuten dan in de vraag staat (vaak het dubbele) bij min+sec → seconden.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },

  // Minuten → uren+min (omgekeerd, met rest)
  {
    id: 'tijd.min-gemengd-1-uur',
    label: 'Maar 1×60 afgetrokken',
    module: 'Tijd',
    description:
      'Leerling trekt slechts 1 × 60 af bij minuten → uren+min, waardoor de rest groter dan 60 blijft.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-gemengd-splitsen',
    label: 'Cijfers gesplitst bij min→uur+min',
    module: 'Tijd',
    description:
      'Leerling splitst het minuten-getal op cijferniveau (bijv. 195 → 19 uur 5 min) i.p.v. te delen door 60.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-gemengd-rest-boven-60',
    label: 'Rest boven 60 laten staan',
    module: 'Tijd',
    description:
      'Leerling laat het rest-aantal minuten boven 60 staan (bijv. 2u75min) i.p.v. door te rekenen.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-gemengd-deel-100',
    label: 'Minuten ÷ 100 i.p.v. ÷ 60',
    module: 'Tijd',
    description:
      'Leerling deelt minuten door 100 in plaats van door 60 bij omrekenen naar uren+minuten.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-gemengd-splitsen-500',
    label: 'Grote cijfers gesplitst (500→50u0min)',
    module: 'Tijd',
    description:
      'Leerling splitst grote minuten-getallen op cijferniveau (bijv. 500 → 50 uur 0 min).',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-gemengd-rest-fout',
    label: 'Rest verkeerd berekend',
    module: 'Tijd',
    description:
      'Leerling berekent het aantal hele uren correct maar de rest-minuten verkeerd.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },

  // Decimale uren ↔ minuten
  {
    id: 'tijd.decimaal-2-5-uur',
    label: '1,5 uur verward met 2,5 uur',
    module: 'Tijd',
    description:
      'Leerling rekent 2,5 × 60 in plaats van 1,5 × 60 (verkeerd getal aan komma).',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.decimaal-komma-weg',
    label: 'Komma genegeerd (1,5 → 15)',
    module: 'Tijd',
    description:
      'Leerling laat de komma weg en behandelt 1,5 als 15 bij omrekenen naar minuten.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.decimaal-0-5-als-45',
    label: '0,5 uur als 45 minuten gerekend',
    module: 'Tijd',
    description:
      'Leerling denkt dat 0,5 uur gelijk is aan 45 minuten in plaats van 30 minuten.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-dec-uur-fout',
    label: 'Deelfout bij min → decimale uren',
    module: 'Tijd',
    description:
      'Leerling deelt door 60 maar maakt een rekenfout en komt op de helft van het juiste antwoord uit.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-dec-uur-deel-10',
    label: 'Min → dec uur ÷ 10 i.p.v. ÷ 60',
    module: 'Tijd',
    description:
      'Leerling deelt minuten door 10 in plaats van door 60 bij omrekenen naar decimale uren.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },
  {
    id: 'tijd.min-dec-uur-deel-6',
    label: 'Min → dec uur ÷ 6 i.p.v. ÷ 60',
    module: 'Tijd',
    description:
      'Leerling deelt minuten door 6 in plaats van door 60 bij omrekenen naar decimale uren.',
    typicalFamilies: ['TIJD-OMREKENEN'],
  },

  // ───────────────────────────────────────────────────────────
  // Gemiddelde snelheid (TIJD-GEMIDDELDE)
  // ───────────────────────────────────────────────────────────
  {
    id: 'tijd.snelheid-als-afstand',
    label: 'Snelheid verward met afstand',
    module: 'Tijd',
    description:
      'Leerling geeft de afgelegde afstand (of het afstandsgetal uit de vraag) als snelheid, zonder te delen door de tijd.',
    typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID'],
  },
  {
    id: 'tijd.snelheid-vermenigvuldigen',
    label: 'Vermenigvuldigt i.p.v. delen bij snelheid',
    module: 'Tijd',
    description:
      'Leerling berekent afstand × tijd in plaats van afstand ÷ tijd bij gemiddelde snelheid.',
    typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID'],
  },
  {
    id: 'tijd.eenheden-optellen',
    label: 'Km en uren bij elkaar opgeteld',
    module: 'Tijd',
    description:
      'Leerling telt afstand en tijd direct bij elkaar op (bijv. 30 km + 2 uur = 32) en negeert de verschillende eenheden.',
    typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID', 'TIJD-AFSTAND'],
  },
  {
    id: 'tijd.komma-tijd-negeren',
    label: 'Kommadeel van tijd genegeerd',
    module: 'Tijd',
    description:
      'Leerling negeert het kommadeel van de tijd (bijv. 2,5 uur → 2 uur) bij snelheidsberekeningen.',
    typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID'],
  },
  {
    id: 'tijd.minuten-niet-omrekenen',
    label: 'Minuten niet omgerekend naar uren',
    module: 'Tijd',
    description:
      'Leerling rekent met minuten alsof het uren zijn bij snelheid in km/u (bijv. 50 min → deelt door 50 i.p.v. 5/6).',
    typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID'],
  },
  {
    id: 'tijd.snelheid-als-tijd',
    label: 'Snelheid verward met tijd in minuten',
    module: 'Tijd',
    description:
      'Leerling neemt het minuten-getal uit de vraag direct als snelheid (bijv. 50 min → 50 km/u).',
    typicalFamilies: ['TIJD-GEMIDDELDE', 'TIJD-SNELHEID'],
  },
  {
    id: 'tijd.snelheid-gemiddeld-naïef',
    label: 'Rekenkundig gemiddelde van snelheden',
    module: 'Tijd',
    description:
      'Leerling neemt (v1 + v2) ÷ 2 als gemiddelde snelheid, ook als de twee stukken niet even lang duren. Werkt alleen bij gelijke tijden, niet bij gelijke afstanden.',
    typicalFamilies: ['TIJD-GEMIDDELDE'],
  },
  {
    id: 'tijd.snelheid-optellen',
    label: 'Snelheden bij elkaar opgeteld',
    module: 'Tijd',
    description:
      'Leerling telt twee snelheden direct bij elkaar op (bijv. 40 + 60 = 100 km/u) als "gemiddelde".',
    typicalFamilies: ['TIJD-GEMIDDELDE'],
  },
  {
    id: 'tijd.snelheid-gemiddeld-rekenfout',
    label: 'Goede aanpak, rekenfout in tussenstap',
    module: 'Tijd',
    description:
      'Leerling gebruikt de juiste formule (totale afstand ÷ totale tijd) maar maakt een rekenfout in een tussenstap.',
    typicalFamilies: ['TIJD-GEMIDDELDE'],
  },
  {
    id: 'tijd.snelheid-stilstand-aftrekken',
    label: 'Stilstand afgetrokken van reistijd',
    module: 'Tijd',
    description:
      'Leerling trekt de stilstandtijd af van de totale reistijd bij gemiddelde reissnelheid, terwijl stilstand juist meetelt.',
    typicalFamilies: ['TIJD-GEMIDDELDE'],
  },
]
